import { useRef, useEffect, useCallback, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import type { ForceGraphData } from '../types/graph';
import { GRAPH_CONFIG } from '../types/graph';

interface Graph2DProps {
  data: ForceGraphData;
  highlightNodes: Set<string> | null;
  onNodeClick?: (node: any) => void;
}

export function Graph2D({ data, highlightNodes, onNodeClick }: Graph2DProps) {
  const fgRef = useRef<any>();
  const startTime = useRef(Date.now());
  const [isStable, setIsStable] = useState(false);

  const log = (stage: string) => {
    const elapsed = Date.now() - startTime.current;
    console.log(`[Graph2D +${elapsed}ms] ${stage}`);
  };

  useEffect(() => {
    log('Component mounted');
  }, []);

  useEffect(() => {
    log(`Data received: ${data.nodes.length} nodes, ${data.links.length} links`);
    setIsStable(false);
  }, [data]);

  const handleNodeClick = useCallback((node: any) => {
    log(`Node clicked: ${node.name}`);
    if (onNodeClick) onNodeClick(node);
  }, [onNodeClick]);

  const handleNodeHover = useCallback((node: any) => {
    const elem = document.body;
    elem.style.cursor = node ? 'pointer' : 'default';
  }, []);

  const handleEngineStop = useCallback(() => {
    log('Physics simulation stopped (graph stabilized)');
    if (fgRef.current) {
      log('Executing zoomToFit after stabilization');
      fgRef.current.zoomToFit(400, 50);
    }
    setIsStable(true);
  }, []);

  const isHighlighting = highlightNodes !== null && highlightNodes.size > 0;

  return (
    <>
      {!isStable && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#fff',
          fontSize: '18px',
          zIndex: 9999
        }}>
          Initializing graph...
        </div>
      )}
      <div style={{ opacity: isStable ? 1 : 0, transition: 'opacity 0.5s' }}>
        <ForceGraph2D
          ref={fgRef}
          graphData={data}

        // Node styling
        nodeLabel={(node: any) => `${node.name}\n${node.service}\n${node.language}`}
        nodeColor={(node: any) =>
          isHighlighting && !highlightNodes!.has(node.id)
            ? 'rgba(80,80,80,0.12)'
            : node.color
        }
        nodeRelSize={GRAPH_CONFIG.nodeRelSize}
        nodeVal={(node: any) => node.val}

        // Link styling
        linkLabel={(link: any) => link.type}
        linkColor={(link: any) => {
          if (!isHighlighting) return link.color;
          const src = typeof link.source === 'object' ? link.source.id : link.source;
          const tgt = typeof link.target === 'object' ? link.target.id : link.target;
          return highlightNodes!.has(src) && highlightNodes!.has(tgt)
            ? link.color
            : 'rgba(80,80,80,0.06)';
        }}
        linkWidth={(link: any) => {
          if (!isHighlighting) return link.width;
          const src = typeof link.source === 'object' ? link.source.id : link.source;
          const tgt = typeof link.target === 'object' ? link.target.id : link.target;
          return highlightNodes!.has(src) && highlightNodes!.has(tgt)
            ? (link.width || 1) * 2
            : link.width;
        }}
        linkDirectionalArrowLength={3.5}
        linkDirectionalArrowRelPos={1}
        linkCurvature={0.25}

        // Physics - smooth natural animation
        d3AlphaDecay={0.0228}
        d3VelocityDecay={0.4}
        cooldownTicks={200}

        // Interactivity
        enableNodeDrag={true}
        enableZoomInteraction={true}
        enablePanInteraction={true}
        enablePointerInteraction={true}

        // Events
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
        onEngineStop={handleEngineStop}
        onBackgroundClick={() => { if (onNodeClick) onNodeClick(null); }}

        // Background
        backgroundColor="#0a0a0a"
        />
      </div>
    </>
  );
}
